/**
 * @file `useMe` — TanStack-backed read of the current learner identity.
 * @module @labcraft/lab/ui/hooks/use-me
 *
 * Replaces the old `app.account.learner` + `account.onChange`
 * external-store subscription. The hook returns the wire-shape
 * `{ learner: AccountLearner | null }` (so anonymous is a payload
 * value, not a loading state); callers branch on `data?.learner` to
 * render the chrome's signed-in vs anonymous branches and to drive
 * the route guards.
 *
 * The 401 callback wired in `Lab`'s constructor calls
 * `setQueryData(qkMe, { learner: null })` synchronously when any
 * authenticated request returns 401. That flip makes this hook
 * re-render with `learner: null` on the next commit — no refetch
 * required. TanStack will also refetch normally on the next mount /
 * refocus per the workspace's `defaultOptions.queries.staleTime`.
 *
 * Convention: hooks live in this directory and never call `useQuery`
 * outside it. Components import workspace hooks (`useMe`, …) only.
 */

import { useQuery } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type { Lab } from '../../client/index.ts'
import type { AccountLearner } from '../../client/account.ts'
import { qkMe } from './query_keys.ts'

/**
 * Wire shape of `useMe()`'s data. Mirrors `MeResponse` but with the
 * dates already flattened to `Date | null` — the same shape the old
 * `account.learner` field exposed, so consumer components don't
 * change beyond `app.account.learner` → `useMe().data?.learner`.
 */
export type MeData = {
  learner: AccountLearner | null
}

/**
 * Subscribe to the current learner from React. The first reader
 * drives the fetch; subsequent readers share the cache. The 401
 * callback's imperative `setQueryData(qkMe, { learner: null })`
 * flips every reader synchronously on the next commit.
 */
export function useMe() {
  const client = useClient<Lab>()
  return useQuery<MeData>({
    queryKey: qkMe,
    queryFn: () => client.account.me(),
  })
}
